// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationDeliveryMethodStyles {
    container?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    notes?: InjectableCss;
    notesHeadingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const onePageCheckoutConfirmationDeliveryMethodStyles: OnePageCheckoutConfirmationDeliveryMethodStyles = {};

const styles = onePageCheckoutConfirmationDeliveryMethodStyles;

const OnePageCheckoutConfirmationDeliveryMethod = ({ cart }: Props) => {
    if (!cart || cart.fulfillmentMethod === FulfillmentMethod.PickUp) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="OnePageCheckoutConfirmationDeliveryMethod">
            <Typography {...styles.titleText}>{translate("Shipping")}</Typography>
            {!!cart.carrier?.description && (
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Shipping Carrier")}
                    text={cart.carrier?.description}
                />
            )}
            {!!cart.shipVia?.description && (
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Shipping Service")}
                    text={cart.shipVia?.description}
                />
            )}
            {!!cart.properties?.carrierName && (
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Name of Carrier")}
                    text={cart.properties?.carrierName}
                />
            )}
            {!!cart.properties?.accountNumber && (
                <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Account Number")}
                    text={cart.properties?.accountNumber}
                />
            )}
            {!!cart.properties?.shippingNotes && (
                <SmallHeadingAndText
                    extendedStyles={styles.notesHeadingAndTextStyles}
                    heading={translate("Shipping Notes")}
                    text={cart.properties?.shippingNotes}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationDeliveryMethod),
    definition: {
        displayName: "Delivery Method",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
